import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { contactData, businessHours, roomCategories } from '../utils/mock';

export const WhatsAppChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [chatData, setChatData] = useState({
    name: '',
    phone: '',
    projectType: '',
    roomType: '',
    budget: '',
    timeline: '',
    location: '',
    additionalInfo: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const checkBusinessHours = () => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    const isWorkingDay = day >= 1 && day <= 6;
    const isWorkingHour = hour >= 10 && hour < 19;
    return isWorkingDay && isWorkingHour;
  };

  const isOnline = checkBusinessHours();

  const conversationSteps = [
    {
      id: 'welcome',
      question: '👋 Welcome to Haarmony Interiors!\n\nI\'m here to help you with your interior design needs. May I know your name?',
      field: 'name',
      type: 'text',
      placeholder: 'Enter your name'
    },
    {
      id: 'phone',
      question: 'Nice to meet you, {name}! 📱\n\nCould you share your phone number so we can reach you?',
      field: 'phone',
      type: 'tel',
      placeholder: '+91 XXXXX XXXXX'
    },
    {
      id: 'projectType',
      question: 'Great! What type of project are you planning? 🏠',
      field: 'projectType',
      type: 'options',
      options: [
        { value: 'residential', label: '🏡 Residential', icon: 'Home' },
        { value: 'commercial', label: '🏢 Commercial', icon: 'Building' },
        { value: 'both', label: '🏘️ Both', icon: 'Building2' }
      ]
    },
    {
      id: 'roomType',
      question: 'Which rooms do you want to design? 🛋️',
      field: 'roomType',
      type: 'options',
      options: [
        { value: 'living', label: '🛋️ Living Room' },
        { value: 'bedroom', label: '🛏️ Bedroom' },
        { value: 'kitchen', label: '🍳 Kitchen' },
        { value: 'bathroom', label: '🛁 Bathroom' },
        { value: 'office', label: '💼 Office' },
        { value: 'complete', label: '🏠 Complete Home/Office' }
      ]
    },
    {
      id: 'budget',
      question: 'What\'s your approximate budget? 💰',
      field: 'budget',
      type: 'options',
      options: [
        { value: 'under-2l', label: 'Under ₹2 Lakhs' },
        { value: '2-5l', label: '₹2-5 Lakhs' },
        { value: '5-10l', label: '₹5-10 Lakhs' },
        { value: 'above-10l', label: 'Above ₹10 Lakhs' },
        { value: 'flexible', label: 'Flexible/Open to discuss' }
      ]
    },
    {
      id: 'timeline',
      question: 'When would you like to start? ⏰',
      field: 'timeline',
      type: 'options',
      options: [
        { value: 'urgent', label: '🔥 Urgent (within 2 weeks)' },
        { value: 'month', label: '📅 Within a month' },
        { value: '2-3months', label: '🗓️ 2-3 months' },
        { value: 'planning', label: '💭 Just planning' }
      ]
    },
    {
      id: 'location',
      question: 'Where is your property located? 📍',
      field: 'location',
      type: 'text',
      placeholder: 'e.g., Kothrud, Pune'
    },
    {
      id: 'additional',
      question: 'Any specific requirements or style preferences? ✨\n\n(You can skip this)',
      field: 'additionalInfo',
      type: 'textarea',
      placeholder: 'e.g., Modern minimalist style, need extra storage...',
      optional: true
    }
  ];

  const currentStepData = conversationSteps[currentStep];

  const handleNext = () => {
    if (currentStep < conversationSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinishAndConnect();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOptionSelect = (value) => {
    setChatData({ ...chatData, [currentStepData.field]: value });
    setTimeout(() => {
      handleNext();
    }, 300);
  };

  const handleInputChange = (value) => {
    setChatData({ ...chatData, [currentStepData.field]: value });
  };

  const handleFinishAndConnect = () => {
    const phoneNumber = contactData.phone.replace(/\s+/g, '');
    
    // Build comprehensive message
    let message = `Hi! I'm ${chatData.name}.\n\n`;
    message += `📞 Phone: ${chatData.phone}\n`;
    message += `🏠 Project Type: ${chatData.projectType}\n`;
    message += `🛋️ Room: ${chatData.roomType}\n`;
    message += `💰 Budget: ${chatData.budget}\n`;
    message += `⏰ Timeline: ${chatData.timeline}\n`;
    message += `📍 Location: ${chatData.location}\n`;
    
    if (chatData.additionalInfo) {
      message += `\n✨ Additional Info:\n${chatData.additionalInfo}`;
    }
    
    message += `\n\nLooking forward to discussing my interior design project!`;
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    // Reset and close
    setIsOpen(false);
    setCurrentStep(0);
    setChatData({
      name: '',
      phone: '',
      projectType: '',
      roomType: '',
      budget: '',
      timeline: '',
      location: '',
      additionalInfo: ''
    });
  };

  const handleSkipToWhatsApp = () => {
    const phoneNumber = contactData.phone.replace(/\s+/g, '');
    const message = chatData.name 
      ? `Hi! I'm ${chatData.name}. I'd like to know more about your interior design services.`
      : "Hi! I'd like to know more about your interior design services.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
  };

  const renderStepContent = () => {
    const step = currentStepData;
    const fieldValue = chatData[step.field];

    return (
      <div className="space-y-4 animate-fade-in">
        {/* Question */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <p className="text-sm text-gray-900 whitespace-pre-line">
            {step.question.replace('{name}', chatData.name)}
          </p>
        </div>

        {/* Input based on type */}
        {step.type === 'text' && (
          <div className="space-y-3">
            <Input
              type="text"
              value={fieldValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={step.placeholder}
              className="w-full"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && fieldValue) {
                  handleNext();
                }
              }}
              autoFocus
            />
          </div>
        )}

        {step.type === 'tel' && (
          <div className="space-y-3">
            <Input
              type="tel"
              value={fieldValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={step.placeholder}
              className="w-full"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && fieldValue) {
                  handleNext();
                }
              }}
              autoFocus
            />
          </div>
        )}

        {step.type === 'textarea' && (
          <div className="space-y-3">
            <textarea
              value={fieldValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={step.placeholder}
              className="w-full min-h-[100px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              autoFocus
            />
          </div>
        )}

        {step.type === 'options' && (
          <div className="space-y-2">
            {step.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect(option.value)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                  fieldValue === option.value
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                }`}
              >
                <span className="font-medium text-gray-900">{option.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-2 pt-2">
          {currentStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          
          {(step.type === 'text' || step.type === 'tel' || step.type === 'textarea') && (
            <Button
              onClick={handleNext}
              disabled={!fieldValue && !step.optional}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              {currentStep === conversationSteps.length - 1 ? 'Connect on WhatsApp' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          
          {step.optional && (
            <Button
              onClick={handleNext}
              variant="outline"
              className="flex-1"
            >
              Skip
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Chat Widget Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <button
            onClick={() => setIsOpen(true)}
            className="group relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full p-4 shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110"
            aria-label="Open WhatsApp Chat"
          >
            <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20"></div>
            <MessageCircle className="h-7 w-7 relative z-10" />
            <div className={`absolute -top-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${
              isOnline ? 'bg-green-400' : 'bg-gray-400'
            }`}></div>
            <div className="absolute -top-1 -left-1 bg-rose-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-bounce">
              1
            </div>
          </button>

          <div className="absolute bottom-full right-0 mb-2 bg-white shadow-xl rounded-lg px-4 py-2 whitespace-nowrap animate-fade-in-up">
            <p className="text-sm font-semibold text-gray-900">Chat with us!</p>
            <p className="text-xs text-gray-600">We typically reply instantly</p>
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full border-2 border-white bg-white flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                  isOnline ? 'bg-green-400' : 'bg-gray-400'
                }`}></div>
              </div>
              <div>
                <h3 className="font-bold text-white">Haarmony Interiors</h3>
                <p className="text-xs text-green-100 flex items-center gap-1">
                  {isOnline ? '● Online - Available now' : 'Offline - Will reply soon'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="bg-gray-200 h-1">
            <div 
              className="bg-green-600 h-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / conversationSteps.length) * 100}%` }}
            ></div>
          </div>

          {/* Chat Body */}
          <div className="bg-gray-50 p-4 max-h-[500px] overflow-y-auto">
            {renderStepContent()}

            {/* Quick skip option */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleSkipToWhatsApp}
                className="w-full text-center text-sm text-gray-600 hover:text-green-600 transition-colors"
              >
                Skip questions & chat directly on WhatsApp →
              </button>
            </div>

            {/* Call Now Option */}
            <button
              onClick={() => window.location.href = `tel:${contactData.phone}`}
              className="w-full mt-3 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg p-3 text-sm font-semibold flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-300"
            >
              <Phone className="h-4 w-4" />
              Or Call: {contactData.phone}
            </button>
          </div>

          {/* Footer */}
          <div className="bg-white p-3 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Step {currentStep + 1} of {conversationSteps.length} • Powered by WhatsApp
            </p>
          </div>
        </div>
      )}
    </>
  );
};

